import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import { getFullUserDetails, toggleUser } from "services";

import { getGremlin, useCancelAsyncOnUnmount, useParams } from "utils";

import { AdminHead, AvatarImage, ErrorBoundary, Icon, MainPage, Tabs } from "components";

import { PageActivityTab } from "pages/admin/users/components/PageActivityTab";
import { TimelineTab } from "pages/admin/users/components/TimelineTab";
import { UserDetailsTab } from "pages/admin/users/components/UserDetailsTab";
import { UserFarmsTab } from "pages/admin/users/components/UserFarmsTab";

export const User = () => {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [show, setShow] = useState({
    views: true,
    methods: true,
    account: true,
  });
  const signal = useCancelAsyncOnUnmount()();

  const createTimeline = useCallback(
    (data) => {
      const timeline = [];

      if (show.account) {
        timeline.push({
          text: "Signed up to Klim",
          date: moment.utc(data.utc).format("DD.MM.YYYY"),
          utc: data.utc,
          icon: <Icon iconName="tabler/mood-smile" className="icon inline" color="green" />,
        });

        if (data.apps.app) {
          timeline.push({
            text: "Signed in to the mobile app for the first time",
            date: moment.utc(data.apps.app).format("DD.MM.YYYY"),
            utc: data.apps.app,
            icon: <Icon iconName="tabler/device-mobile" className="icon inline" color="black" />,
          });
        }

        if (data.apps.mobile) {
          timeline.push({
            text: "Signed in to the mobile website for the first time",
            date: moment.utc(data.apps.mobile).format("DD.MM.YYYY"),
            utc: data.apps.mobile,
            icon: <Icon iconName="tabler/device-mobile" className="icon inline" color="black" />,
          });
        }

        if (data.apps.desktop) {
          timeline.push({
            text: "Signed in to the desktop website for the first time",
            date: moment.utc(data.apps.desktop).format("DD.MM.YYYY"),
            utc: data.apps.desktop,
            icon: <Icon iconName="tabler/device-laptop" className="icon inline" color="black" />,
          });
        }

        if (data.signedin) {
          timeline.push({
            text: "Last signed in",
            date: moment.utc(data.signedinutc).format("DD.MM.YYYY"),
            utc: data.signedinutc,
            icon: <Icon iconName="tabler/mood-smile" className="icon inline" color="black" />,
          });
        }

        if (data.farmCreated) {
          timeline.push({
            text: "Created a farm",
            date: moment.utc(data.farmCreated).format("DD.MM.YYYY"),
            utc: data.farmCreated,
            icon: <Icon iconName="tabler/tractor" className="icon inline" color="green" />,
          });
        }

        if (data.farmUpated) {
          timeline.push({
            text: "Updated their farm",
            date: moment.utc(data.farmUpated).format("DD.MM.YYYY"),
            utc: data.farmUpated,
            icon: <Icon iconName="tabler/tractor" className="icon inline" color="grey" />,
          });
        }

        if (data.farmOnboarded) {
          timeline.push({
            text: "Onboarded their farm",
            date: moment.utc(data.farmOnboarded).format("DD.MM.YYYY"),
            utc: data.farmOnboarded,
            icon: <Icon iconName="tabler/tractor" className="icon inline" color="black" />,
          });
        }

        if (data.contentOnboarded) {
          timeline.push({
            text: "Onboarded their content preferences",
            date: moment.utc(data.contentOnboarded.date).format("DD.MM.YYYY"),
            utc: data.contentOnboarded.date,
            icon: <Icon iconName="tabler/book" className="icon inline" color="black" />,
          });
        }
      }

      if (show.views) {
        data.pages.forEach((item) => {
          timeline.push({
            text: `Viewed ${item.page}`,
            date: moment.utc(item.date).format("DD.MM.YYYY"),
            utc: item.date,
            icon: <Icon iconName="tabler/eye" className="icon inline" color="grey" />,
          });
        });
      }

      if (show.methods) {
        data.methods.forEach((item) => {
          timeline.push({
            text: `Add the method "${item.method}"`,
            date: moment.utc(item.date).format("DD.MM.YYYY"),
            utc: item.date,
            icon: <Icon iconName="tabler/backhoe" className="icon inline" color="black" />,
          });
        });

        data.verifications.forEach((item) => {
          timeline.push({
            text: `Submitted a verification for "${item.method}"`,
            date: moment.utc(item.date).format("DD.MM.YYYY"),
            utc: item.date,
            icon: <Icon iconName="tabler/award" className="icon inline" color="black" />,
          });
        });
      }

      timeline.sort((a, b) => b.utc - a.utc);

      setTimeline(timeline);
    },
    [show]
  );

  const loadUser = useCallback(() => {
    setLoading(true);

    getFullUserDetails({
      id: id,
      signal,
    })
      .then((res) => {
        setUser(res);
        createTimeline(res);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [createTimeline, id, signal]);

  const toggleCurrentUser = () => {
    toggleUser({
      id: id,
      signal,
    })
      .then(() => {
        loadUser();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    user && (
      <MainPage loading={loading} pageName={"admin"}>
        <AdminHead
          headline={
            <>
              {!user.image ? (
                <AvatarImage image={getGremlin()} className="margin-right-100" />
              ) : (
                <AvatarImage image={user.image} className="margin-right-100" />
              )}{" "}
              {user.name}
            </>
          }
          link="/users/users"
          tooltipText="Back to users"
        />

        <div className="content">
          <Tabs
            size="small"
            items={[
              {
                label: "User details",
                key: "user",
                children: (
                  <ErrorBoundary>
                    <UserDetailsTab user={user} toggleCurrentUser={toggleCurrentUser} />
                  </ErrorBoundary>
                ),
              },
              {
                label: "Timeline",
                key: "timeline",
                children: (
                  <ErrorBoundary>
                    <TimelineTab createTimeline={createTimeline} setShow={setShow} show={show} timeline={timeline} user={user} />
                  </ErrorBoundary>
                ),
              },
              {
                label: "Page activity",
                key: "page-activity",
                children: (
                  <ErrorBoundary>
                    <PageActivityTab user={user} />
                  </ErrorBoundary>
                ),
              },
              {
                label: "Farms",
                key: "user-farms",
                children: (
                  <ErrorBoundary>
                    <UserFarmsTab user={user} />
                  </ErrorBoundary>
                ),
              },
            ]}
          />
        </div>
      </MainPage>
    )
  );
};
